import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { doc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { toast } from 'react-toastify';
import { LuDownload, LuExternalLink, LuFileText, LuLoaderCircle, LuPencil, LuSave, LuTrash2 } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { Field, SelectInput, TextArea, TextInput } from '../../../components/form/FormControls';
import { db, storage } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import PdfPicker from './PdfPicker';
import { EXPENSE_CATEGORIES, attachedPdfs } from './expenseOptions';

const linkButton = 'tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50';

/**
 * "Masraf Talebini Düzenle" (pending requests only): amount, category,
 * description and the PDFs. New PDFs replace the attached ones; the attached
 * ones can also be removed.
 */
const EditExpenseDialog = ({ expense, employeeId, onClose, onSaved }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.expense.edit.${key}`, vars);
  const [editExpenseAmount, setEditExpenseAmount] = useState('');
  const [editExpenseCategory, setEditExpenseCategory] = useState('');
  const [editExpenseDescription, setEditExpenseDescription] = useState('');
  const [editExpensePdfFiles, setEditExpensePdfFiles] = useState([]);
  const [keepExistingPdf, setKeepExistingPdf] = useState(true);
  const [editExpenseLoading, setEditExpenseLoading] = useState(false);
  const [editExpensePdfUploading, setEditExpensePdfUploading] = useState(false);

  useEffect(() => {
    if (!expense) return;
    setEditExpenseAmount(expense.amount || '');
    setEditExpenseCategory(expense.category || 'Diğer');
    setEditExpenseDescription(expense.description || '');
    setEditExpensePdfFiles([]);
    setKeepExistingPdf(attachedPdfs(expense).length > 0);
  }, [expense]);

  const existing = expense ? attachedPdfs(expense) : [];

  const handleExpenseUpdate = async () => {
    if (!expense) return;
    if (!editExpenseAmount || parseFloat(editExpenseAmount) <= 0 || !editExpenseDescription) {
      toast.error(t('profilePage.expense.form.errors.required'));
      return;
    }

    setEditExpenseLoading(true);
    try {
      let pdfUrls = [];
      let pdfFileNames = [];
      if (editExpensePdfFiles.length > 0) {
        setEditExpensePdfUploading(true);
        const timestamp = Date.now();
        try {
          const uploadResults = await Promise.all(editExpensePdfFiles.map(async (file, index) => {
            const fileName = `expense-${employeeId}-${timestamp}-${index + 1}.pdf`;
            const snapshot = await uploadBytes(ref(storage, `expense-documents/${fileName}`), file);
            const url = await getDownloadURL(snapshot.ref);
            return { url, fileName: file.name };
          }));
          pdfUrls = uploadResults.map((result) => result.url);
          pdfFileNames = uploadResults.map((result) => result.fileName);
          setEditExpensePdfUploading(false);
        } catch (uploadError) {
          console.error('❌ PDF upload error:', uploadError);
          toast.error(t('profilePage.expense.form.errors.uploadDetail', { message: uploadError.message }));
          setEditExpensePdfUploading(false);
          setEditExpenseLoading(false);
          return;
        }
      } else if (keepExistingPdf) {
        // Mevcut PDF'ler korunur (yeni biçim dizi, eski biçim tek dosya)
        if (Array.isArray(expense.pdfUrls) && expense.pdfUrls.length > 0) {
          pdfUrls = [...expense.pdfUrls];
          pdfFileNames = expense.pdfFileNames ? [...expense.pdfFileNames] : [];
        } else if (expense.pdfUrl) {
          pdfUrls = [expense.pdfUrl];
          pdfFileNames = expense.pdfFileName ? [expense.pdfFileName] : ['Masraf Belgesi.pdf'];
        }
      }

      const updateData = {
        amount: parseFloat(editExpenseAmount),
        category: editExpenseCategory,
        description: editExpenseDescription,
        updatedAt: new Date().toISOString(),
        pdfUrls,
        pdfFileNames,
        // Eski kayıtlarla uyum için tek dosya alanları
        pdfUrl: pdfUrls.length > 0 ? pdfUrls[0] : null,
        pdfFileName: pdfUrls.length > 0 ? (pdfFileNames[0] || 'Masraf Belgesi.pdf') : null,
      };

      await updateDoc(doc(db, 'ExpenseRequests', expense.id), updateData);
      toast.success(label('saved'));
      onSaved(expense.id, updateData);
      onClose();
    } catch (error) {
      console.error('❌ Masraf güncelleme hatası:', error);
      toast.error(label('saveError', { message: error.message }));
    } finally {
      setEditExpenseLoading(false);
      setEditExpensePdfUploading(false);
    }
  };

  const downloadPdf = (url, name, index) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name || `masraf-belgesi-${index + 1}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(label('downloading'));
  };

  const busy = editExpenseLoading || editExpensePdfUploading;
  const showExisting = keepExistingPdf && editExpensePdfFiles.length === 0 && existing.length > 0;

  return (
    <Modal
      isOpen={Boolean(expense)}
      onClose={() => !busy && onClose()}
      size="lg"
      title={label('title')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" size="xs">
          <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy} className="max-sm:tw-hidden">{t('profilePage.common.cancel')}</Button>
          <Button variant="primary" onClick={handleExpenseUpdate} disabled={busy} className="max-sm:tw-flex-1">
            {busy ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {busy ? label('saving') : t('profilePage.common.save')}
          </Button>
        </>
      )}
    >
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={t('profilePage.expense.form.amount')} htmlFor="edit-expense-amount" required>
          <TextInput id="edit-expense-amount" type="number" step="0.01" min="0" inputMode="decimal" value={editExpenseAmount} onChange={(event) => setEditExpenseAmount(event.target.value)} placeholder="150.00" />
        </Field>
        <Field label={t('profilePage.expense.form.category')} htmlFor="edit-expense-category" required>
          <SelectInput
            id="edit-expense-category"
            value={editExpenseCategory}
            current={editExpenseCategory}
            onChange={(event) => setEditExpenseCategory(event.target.value)}
            options={EXPENSE_CATEGORIES.map((category) => ({ value: category.value, label: t(`profilePage.expense.categories.${category.key}`) }))}
          />
        </Field>
        <Field label={t('profilePage.expense.form.descriptionLabel')} htmlFor="edit-expense-description" required className="sm:tw-col-span-2">
          <TextArea id="edit-expense-description" rows={4} value={editExpenseDescription} onChange={(event) => setEditExpenseDescription(event.target.value)} placeholder={t('profilePage.expense.form.descriptionPlaceholder')} />
        </Field>

        {showExisting && (
          <div className="tw-space-y-1.5 sm:tw-col-span-2">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-0.5">
              <span className="tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('existing')}</span>
              <button
                type="button"
                onClick={() => {
                  setKeepExistingPdf(false);
                  toast.success(label('existingRemoved'));
                }}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-transparent tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-rose-600 hover:tw-bg-rose-500/10 dark:tw-text-rose-300"
              >
                <LuTrash2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                {label('removeExisting')}
              </button>
            </div>
            <ul className="tw-space-y-1.5">
              {existing.map((pdf, index) => (
                <li key={pdf.url} className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-emerald-500/25 tw-bg-emerald-500/[0.06] tw-px-3 tw-py-2">
                  <LuFileText aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-emerald-600" />
                  <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium tw-text-emerald-900 dark:tw-text-emerald-100">{pdf.name || label('documentN', { n: index + 1 })}</span>
                  <a href={pdf.url} target="_blank" rel="noopener noreferrer" title={label('view')} aria-label={label('view')} className={`${linkButton} tw-text-sky-700 hover:tw-bg-sky-500/10 dark:tw-text-sky-300`}>
                    <LuExternalLink aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </a>
                  <button type="button" onClick={() => downloadPdf(pdf.url, pdf.name, index)} title={label('download')} aria-label={label('download')} className={`${linkButton} tw-text-indigo-700 hover:tw-bg-indigo-500/10 dark:tw-text-indigo-300`}>
                    <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Field
          label={showExisting ? label('replace') : label('add')}
          htmlFor="edit-expense-pdf-input"
          hint={showExisting ? label('replaceHint') : undefined}
          className="sm:tw-col-span-2"
        >
          <PdfPicker
            id="edit-expense-pdf-input"
            files={editExpensePdfFiles}
            onChange={(files) => {
              setEditExpensePdfFiles(files);
              // Yeni dosya seçilince eklilerin yerine geçer
              if (files.length > 0) setKeepExistingPdf(false);
            }}
            disabled={busy}
          />
        </Field>
      </div>
    </Modal>
  );
};

EditExpenseDialog.propTypes = {
  expense: PropTypes.object,
  employeeId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default EditExpenseDialog;
